import React, { CSSProperties, FC, useCallback, useMemo, useState } from 'react';
import SimpleSelectionListDropDown from '../selectionlists/SimpleSelectionListDropDown';
import { concreteLoanFormatKeyToObject, LOANFORMAT_EPRESS } from '../../store/loanFormat/actions';

import TopChartRow from './TopChartRow';
import { Orderlist } from '../../../models/OrderList';
import { ProductMetaData } from '../../../models/Product';
import { LoanFormat } from '../../../models/LoanFormat';
import { Button, ButtonSize, ButtonTheme } from '../../shared/common/Button';
import { MATERIAL_ICON_CART } from '../../shared/common/Button/Icon';
import { Trans, useTranslation } from 'react-i18next';
import { BulkAddProductsToSelectionListResponse, BulkAddProductsToShoppingCartResponse } from '../../store/api';
import { ListActionTarget } from '../../../models/ListActionTarget';
import {
    isTopChartAddToListOptions,
    TopChartAddToCartOptions,
    TopChartAddToListOptions
} from '../../../models/ListContentCheckOptions';
import { useListContentChecks } from '../../shared/common/useListContentChecks';
import { CheckBeforeAddingModal } from '../../shared/common/CheckBeforeAddingModal';
import { UnavailableProductsModal } from '../../shared/common/UnavailableProductsModal';
import { useAddTopChartProductsToCart } from '../../api/shopping-cart/useAddTopChartProductsToCart';
import { useAddTopChartProductsToList } from '../../api/selection-list/useAddTopChartProductsToList';
import { ConfirmOptionsModal, DEFAULT_SUBSCRIPTION_PERIOD_TYPE } from '../../shared/common/ConfirmOptionsModal';
import { PriceTier } from '../../../models/PriceTier';
import { ACTION_ADD } from '../../store/constants';

type Props = {
    readonly loanFormatKey?: LoanFormat;
    readonly products?: ReadonlyArray<ProductMetaData>;
    readonly selectionLists?: ReadonlyArray<Orderlist>;
    readonly shopLanguage: string | undefined;
};

const ARROW_STYLE: CSSProperties = { color: 'black', fontSize: '14px', float: 'right' };

export const TopCharts: FC<Props> = ({ loanFormatKey, products, selectionLists, shopLanguage }) => {
    const [addOptions, setAddOptions] = useState<TopChartAddToListOptions | TopChartAddToCartOptions | undefined>(
        undefined
    );
    const loanFormat = concreteLoanFormatKeyToObject((loanFormatKey || '').toUpperCase());
    const { t } = useTranslation();
    const noStockProducts = products?.filter(product => !product.copyStockAmount);

    const { addToCartBulk } = useAddTopChartProductsToCart(); // note: we don't have to invalidate the products call, since we don't show a "items in cart" icon on this page
    const { addToListBulk } = useAddTopChartProductsToList();
    const defaultPriceTier: PriceTier = useMemo(
        () =>
            loanFormat?.key === LOANFORMAT_EPRESS
                ? { lendingModel: 'SIM', level: 'MIN' }
                : { lendingModel: 'OCOU', level: 'MIN' },
        [loanFormat?.key]
    );

    const onBulkAdd = useCallback(
        (
            performChecks: boolean,
            options: TopChartAddToListOptions | TopChartAddToCartOptions
        ): Promise<BulkAddProductsToSelectionListResponse | BulkAddProductsToShoppingCartResponse> => {
            const selectedProducts = options.includeAllProducts && products ? products : noStockProducts ?? [];

            return isTopChartAddToListOptions(options)
                ? addToListBulk(
                      options.list.id,
                      selectedProducts,
                      performChecks,
                      options.priceTier,
                      options.subscriptionPeriodType
                  )
                : addToCartBulk(selectedProducts, performChecks, options.priceTier, options.subscriptionPeriodType);
        },
        [noStockProducts, addToCartBulk, addToListBulk, products]
    );

    const { changePrice, getCheckBeforeAddingModalProps, getUnavailableProductsModalProps } = useListContentChecks<
        TopChartAddToListOptions | TopChartAddToCartOptions
    >({ onAction: onBulkAdd });

    const handleBulkAddAllToList = useCallback(
        (list: Orderlist) =>
            setAddOptions({
                target: ListActionTarget.List,
                list,
                includeAllProducts: true,
                priceTier: defaultPriceTier,
                subscriptionPeriodType: DEFAULT_SUBSCRIPTION_PERIOD_TYPE,
                action: ACTION_ADD
            }),
        [defaultPriceTier]
    );

    const handleBulkAddNoStocksToList = useCallback(
        (list: Orderlist) =>
            setAddOptions({
                target: ListActionTarget.List,
                list,
                includeAllProducts: false,
                priceTier: defaultPriceTier,
                subscriptionPeriodType: DEFAULT_SUBSCRIPTION_PERIOD_TYPE,
                action: ACTION_ADD
            }),
        [defaultPriceTier]
    );

    const handleBulkAddAllToCart = useCallback(
        () =>
            setAddOptions({
                target: ListActionTarget.Cart,
                includeAllProducts: true,
                priceTier: defaultPriceTier,
                subscriptionPeriodType: DEFAULT_SUBSCRIPTION_PERIOD_TYPE,
                action: ACTION_ADD
            }),
        [defaultPriceTier]
    );

    const handleBulkAddNoStocksToCart = useCallback(
        () =>
            setAddOptions({
                target: ListActionTarget.Cart,
                includeAllProducts: false,
                priceTier: defaultPriceTier,
                subscriptionPeriodType: DEFAULT_SUBSCRIPTION_PERIOD_TYPE,
                action: ACTION_ADD
            }),
        [defaultPriceTier]
    );

    if (!loanFormat || !products || products.length === 0) return null;

    const Icon = () => (
        <span
            key={`tc-${loanFormat.key}-icon`}
            className="copy-stock-warning"
            style={{ display: 'inline-block', margin: '0 2px' }}
        >
            <i className="material-icons fl-left smaller" style={{ marginRight: '1px' }}>
                content_copy
            </i>
            0
        </span>
    );

    return (
        <div className="top-charts">
            <div>
                <h2>
                    <i className="bolinda-icons">{loanFormat.icon}</i>
                    <span>{t(`components.topCharts.top_${loanFormat.name}`, {})}</span>
                </h2>
                {products.map((product, i) => {
                    return (
                        <TopChartRow
                            key={`top-product-${i}`}
                            product={product}
                            rank={i + 1}
                            shopLanguage={shopLanguage}
                        />
                    );
                })}
            </div>
            <div className="top-charts-button-group" style={{ marginTop: '18px' }}>
                <div style={{ display: 'inline-block' }}>
                    <SimpleSelectionListDropDown
                        arrowStyle={ARROW_STYLE}
                        iconCode="add"
                        htmlLabel={<Trans i18nKey="addToListWithIcon" components={{ 1: <Icon /> }} />}
                        selectionLists={selectionLists!}
                        onBulkAddToList={handleBulkAddNoStocksToList}
                    />
                    <a onClick={handleBulkAddNoStocksToCart} className="bt bt-bordered">
                        <i className="material-icons normal fl-left">shopping_cart</i>
                        <Trans i18nKey="addToCartWithIcon" components={{ 1: <Icon /> }} />
                    </a>
                </div>
                <div style={{ display: 'inline-block' }}>
                    <SimpleSelectionListDropDown
                        arrowStyle={ARROW_STYLE}
                        iconCode="add"
                        selectionLists={selectionLists!}
                        htmlLabel={t('addAllToList')}
                        onBulkAddToList={handleBulkAddAllToList}
                    />

                    <Button
                        text={t('addAllToCart')}
                        onClick={handleBulkAddAllToCart}
                        icon={MATERIAL_ICON_CART}
                        size={ButtonSize.SMALL}
                        theme={ButtonTheme.NEUTRAL}
                    />
                </div>
            </div>

            <CheckBeforeAddingModal {...getCheckBeforeAddingModalProps()} />
            <UnavailableProductsModal {...getUnavailableProductsModalProps()} />
            <ConfirmOptionsModal
                options={addOptions}
                changePrice={changePrice}
                showUsageConditionOption={true}
                showSubscriptionStartDateOption={loanFormat?.key === LOANFORMAT_EPRESS}
                closeModal={() => setAddOptions(undefined)}
            />
        </div>
    );
};
